import { html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WEB_COMPONENT_HEALTH_DASHBOARD_SECURITY_SERVER_STATUS } from '../../../../webcomponents.constants';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import {
  HealthDashboardSecurityServersStatusData,
  SecurityServersStatusItem,
  SecurityServersStatusStringsSection,
} from '../types';
import { translateFn } from '../../../../utils/translator.helper';
import { WidgetHeaderItemSize } from '../../../../ui/gzWidgetHeaderItem/types';
import { getTranslatedWidgetTitle } from '../../../../genericDashboard/utils/translations';
import { HealthDashboardInfoStateData, SecurityServersStatusData } from '@nirvana/shared/dtos/types/widgets/common';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';

@customElement(WEB_COMPONENT_HEALTH_DASHBOARD_SECURITY_SERVER_STATUS)
export class SecurityServerStatusWebComponent extends GzGenericWidgetContent {
  @property({ type: Object, attribute: true, reflect: true })
  payload: { securityServersStatus: HealthDashboardSecurityServersStatusData };

  changedInfoBoxText = `
    <gz-tooltip isEllipsis tooltipText="${translateFn('You have <b>updates</b> for this widget!')}">
      ${translateFn('You have <b>updates</b> for this widget!')}
    </gz-tooltip>
    <br />
    <br />
    <gz-tooltip
      isEllipsis
      class="three-line-tooltip"
      tooltipText="${translateFn(
        'To experience the latest design, close the current widget and select the new ones in the Settings panel. Apply your changes and save the view.'
      )}"
    >
      ${translateFn(
        'To experience the latest design, close the current widget and select the new ones in the Settings panel. Apply your changes and save the view.'
      )}
    </gz-tooltip>`;

  strings: SecurityServersStatusStringsSection = {
    items: [
      {
        key: 'outdated',
        name: translateFn('Outdated (Security Content)'),
        barColor: 'var(--yellow-alert-050)',
      },
      {
        key: 'turnedOff',
        name: translateFn('Stopped'),
        barColor: 'var(--red-chart-600)',
      },
      {
        key: 'storageScanningEnabled',
        name: translateFn('Storage scanning enabled'),
        barColor: 'var(--green-teal-chart-300)',
      },
      {
        key: 'storageScanningDisabled',
        name: translateFn('Storage scanning disabled'),
        barColor: 'var(--orange-alert-chart-100)',
      },
    ],
  };

  static get styles() {
    return styles;
  }

  protected renderLoader(): TemplateResult {
    return html`
      <div class="loading-container">
        <div class="loading-header">
          <gz-loader></gz-loader>
          <div class="vertical-bar"></div>
          <gz-loader></gz-loader>
          <div class="vertical-bar"></div>
          <gz-loader></gz-loader>
        </div>
        <div class="loading-section">
          <gz-loader rows="${this.strings.items.length}"></gz-loader>
        </div>
      </div>
    `;
  }

  protected renderContent(): TemplateResult {
    return html`<div id="security-server-status">
      ${this.renderHeader()}
      <div class="section">${this.renderInfoBox() ?? this.strings.items.map(item => this.renderItem(item))}</div>
    </div>`;
  }

  protected renderInfoBox(): TemplateResult | undefined {
    if (this.state === WidgetStateFrontEnd.IS_CHANGED) {
      return this.renderChangedInfoBox(this.changedInfoBoxText);
    }
    const hasInheritedLicense = (this.payload?.securityServersStatus as HealthDashboardInfoStateData)
      ?.hasInheritedLicense;
    let noLicenseText = translateFn(
      "No information available. To retrieve data about your company's {widgetName}, please consider a compatible {licenseLink} key.",
      {
        widgetName: '<b>' + getTranslatedWidgetTitle(WEB_COMPONENT_HEALTH_DASHBOARD_SECURITY_SERVER_STATUS) + '</b>',
        licenseLink: `<gz-link type="underline" href="https://www.bitdefender.com/business/compare.html"><span>${translateFn(
          'license'
        )}</span></gz-link>`,
      }
    );

    if (hasInheritedLicense) {
      noLicenseText = translateFn(
        "To retrieve data about your company's {widgetName}, please contact your Bitdefender Partner and upgrade the licensing plan.",
        {
          widgetName: '<b>' + getTranslatedWidgetTitle(WEB_COMPONENT_HEALTH_DASHBOARD_SECURITY_SERVER_STATUS) + '</b>',
        }
      );
    }

    return super.renderNoLicenseInfoBox(noLicenseText);
  }

  protected renderHeader(): TemplateResult {
    const activeStateData = this.payload?.securityServersStatus as SecurityServersStatusData;
    const decreaseNameFont = this.decreaseNameFont();
    return html`
      <div class="header">
        ${this.renderHeaderItem(
          this.getHeaderItemStrings()[0],
          decreaseNameFont,
          activeStateData?.overview?.totalCount ?? 0
        )}
        <div class="vertical-bar"></div>
        ${this.renderHeaderItem(
          this.getHeaderItemStrings()[1],
          decreaseNameFont,
          activeStateData?.overview?.underloaded ?? 0
        )}
        <div class="vertical-bar"></div>
        ${this.renderHeaderItem(
          this.getHeaderItemStrings()[2],
          decreaseNameFont,
          activeStateData?.overview?.overloaded ?? 0
        )}
      </div>
    `;
  }

  protected renderHeaderItem(name: string, decreaseNameFont = false, value?: number): TemplateResult {
    const size = this.getWidgetHeaderItemSize();
    return html`
      <gz-widget-header-item
        .name="${name}"
        .value="${value ?? 0}"
        .size="${size}"
        .state="${this.state}"
        ?decreaseNameFont="${decreaseNameFont || size === WidgetHeaderItemSize.SMALL}"
      ></gz-widget-header-item>
    `;
  }

  protected getWidgetHeaderItemSize(): WidgetHeaderItemSize {
    if (!this.hasResizeLimits() && this.currentWidthInterval[1] === this.resizeBreakingPoints[0]) {
      return WidgetHeaderItemSize.SMALL;
    }
    if (
      !this.hasResizeLimits() &&
      this.currentWidthInterval[0] === this.resizeBreakingPoints[0] &&
      this.currentWidthInterval[1] === this.resizeBreakingPoints[1]
    ) {
      return WidgetHeaderItemSize.MEDIUM;
    }
    return WidgetHeaderItemSize.BIG;
  }

  protected renderItem(item: SecurityServersStatusItem): TemplateResult | undefined {
    const activeStateData = this.payload?.securityServersStatus as SecurityServersStatusData;

    const value = activeStateData?.hasOwnProperty('serversStatus')
      ? activeStateData.serversStatus[item.key]
      : undefined;

    /* storageScanningEnabled or storageScanningDisabled are optional fields from backend
       second condition is mandatory for error state, otherwise, empty widget will be rendered */
    if (value === undefined && item.key.includes('storageScanning')) {
      return;
    }

    const maxValue = activeStateData?.hasOwnProperty('overview') ? activeStateData.overview?.totalCount : undefined;
    if (!this.hasResizeLimits() && this.currentWidthInterval[1] === this.resizeBreakingPoints[0]) {
      return html` <gz-widget-section-item .name=${item.name} .value="${value}" .state=${this.state} /> `;
    } else {
      return html`
        <gz-widget-section-item
          .name=${item.name}
          .value="${value}"
          .progressMaxValue=${maxValue}
          .progressValue="${value}"
          .progressColor=${item.barColor}
          .state=${this.state}
        />
      `;
    }
  }

  private decreaseNameFont(): boolean {
    return this.getHeaderItemStrings().some(item => item.length > 10);
  }

  private getHeaderItemStrings(): string[] {
    return [translateFn('Total'), translateFn('Underloaded'), translateFn('Overloaded')];
  }
}
